import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Grid
} from '@mui/material';
import { ArrowLeft, Calendar, Tag, AlertTriangle, CheckCircle, TrendingUp, BarChart3, User, Database } from 'lucide-react';
import { analysisInsightsApiService, AnalysisInsightResponse } from '@/services/api/analysisInsights';
import { toast } from 'sonner';

const getImportanceColor = (importance: string) => {
  switch (importance.toLowerCase()) {
    case 'high': return 'error';
    case 'medium': return 'warning';
    case 'low': return 'info';
    default: return 'default';
  }
};

const getAnalysisTypeIcon = (analysisType: string) => {
  switch (analysisType) {
    case 'price_analysis': return <TrendingUp className="h-5 w-5" />;
    case 'supplier_analysis': return <BarChart3 className="h-5 w-5" />;
    case 'trend_analysis': return <TrendingUp className="h-5 w-5" />;
    default: return <BarChart3 className="h-5 w-5" />;
  }
};

const formatAnalysisType = (type: string) => {
  return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const DataSummaryCard = ({ data }: { data: Record<string, any> }) => {
  if (!data || Object.keys(data).length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <Typography variant="h6" className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Data Summary
        </Typography>
      </CardHeader>
      <CardContent>
        <Grid container spacing={2}>
          {Object.entries(data).map(([key, value]) => (
            <Grid item xs={12} sm={6} md={4} key={key}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                  {key.replace(/_/g, ' ')}
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {typeof value === 'number' 
                    ? value.toLocaleString() 
                    : String(value)
                  }
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default function AnalysisInsightDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [insight, setInsight] = useState<AnalysisInsightResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchInsight();
    }
  }, [id]);

  const fetchInsight = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);
      const data = await analysisInsightsApiService.getAnalysisInsightById(id);
      setInsight(data);
    } catch (err) {
      setError('Failed to load analysis insight. Please try again later.');
      console.error('Error fetching insight:', err);
      toast.error('Failed to load analysis insight');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
        <Box mt={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate('/app/analysis')}
          >
            Back to Analysis
          </Button>
        </Box>
      </Box>
    );
  }

  if (!insight) {
    return (
      <Box p={3}>
        <Alert severity="warning">Analysis insight not found.</Alert>
        <Box mt={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate('/app/analysis')}
          >
            Back to Analysis
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Box mb={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowLeft className="h-4 w-4" />}
          onClick={() => navigate('/app/analysis')}
          sx={{ mb: 2 }}
        >
          Back to Analysis
        </Button>

        <Typography variant="h4" component="h1" gutterBottom>
          Analysis Insight Detail
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Original Query */}
          <Card sx={{ mb: 3 }}>
            <CardHeader>
              <Typography variant="h6">Original Query</Typography>
            </CardHeader>
            <CardContent>
              <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                "{insight.original_query}"
              </Typography>
            </CardContent>
          </Card>

          {/* Analysis Text */}
          <Card sx={{ mb: 3 }}>
            <CardHeader>
              <Typography variant="h6">Analysis Result</Typography>
            </CardHeader>
            <CardContent>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                {insight.analysis_text}
              </Typography>
            </CardContent>
          </Card>

          {/* Recommendations */}
          {insight.recommendations && insight.recommendations.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardHeader>
                <Typography variant="h6">Recommendations</Typography>
              </CardHeader>
              <CardContent>
                <List>
                  {insight.recommendations.map((rec, index) => (
                    <ListItem key={index} sx={{ pl: 0 }}>
                      <ListItemText
                        primary={`${index + 1}. ${rec}`}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontSize: '1rem',
                            lineHeight: 1.5
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}

          {/* Data Summary */}
          <DataSummaryCard data={insight.data_summary} />

          {/* SQL Query (if available) */}
          {insight.sql_query && (
            <Card sx={{ mb: 3 }}>
              <CardHeader>
                <Typography variant="h6">SQL Query</Typography>
              </CardHeader>
              <CardContent>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="body2" component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                    {insight.sql_query}
                  </Typography>
                </Paper>
              </CardContent>
            </Card>
          )}

          {/* Notes (if available) */}
          {insight.notes && (
            <Card sx={{ mb: 3 }}>
              <CardHeader>
                <Typography variant="h6">Notes</Typography>
              </CardHeader>
              <CardContent>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                  {insight.notes}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Metadata */}
          <Card sx={{ mb: 3 }}>
            <CardHeader>
              <Typography variant="h6">Metadata</Typography>
            </CardHeader>
            <CardContent>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" alignItems="center" gap={2}>
                  <Calendar className="h-4 w-4" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Created
                    </Typography>
                    <Typography variant="body2">
                      {new Date(insight.timestamp).toLocaleDateString()} at{' '}
                      {new Date(insight.timestamp).toLocaleTimeString()}
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                <Box display="flex" alignItems="center" gap={2}>
                  {getAnalysisTypeIcon(insight.analysis_type)}
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Analysis Type
                    </Typography>
                    <Typography variant="body2">
                      {formatAnalysisType(insight.analysis_type)}
                    </Typography>
                  </Box>
                </Box>

                <Divider />

                <Box display="flex" alignItems="center" gap={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Importance
                    </Typography>
                    <Box mt={0.5}>
                      <Chip
                        label={insight.importance.charAt(0).toUpperCase() + insight.importance.slice(1)}
                        color={getImportanceColor(insight.importance) as any}
                        size="small"
                      />
                    </Box>
                  </Box>
                </Box>

                <Divider />

                <Box display="flex" alignItems="center" gap={2}>
                  {insight.requires_action ? (
                    <AlertTriangle className="h-4 w-4 text-warning" />
                  ) : (
                    <CheckCircle className="h-4 w-4 text-success" />
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Requires Action
                    </Typography>
                    <Typography variant="body2" color={insight.requires_action ? 'warning.main' : 'success.main'}>
                      {insight.requires_action ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                </Box>

                {insight.user_id && (
                  <>
                    <Divider />
                    <Box display="flex" alignItems="center" gap={2}>
                      <User className="h-4 w-4" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          User ID
                        </Typography>
                        <Typography variant="body2">
                          {insight.user_id}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}

                {insight.result_count && (
                  <>
                    <Divider />
                    <Box display="flex" alignItems="center" gap={2}>
                      <Database className="h-4 w-4" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Result Count
                        </Typography>
                        <Typography variant="body2">
                          {insight.result_count.toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Tags */}
          {insight.tags && insight.tags.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardHeader>
                <Typography variant="h6" className="flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Tags
                </Typography>
              </CardHeader>
              <CardContent>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {insight.tags.map((tag, index) => (
                    <Chip 
                      key={index}
                      label={tag} 
                      size="small" 
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Action Taken */}
          {insight.action_taken && (
            <Card sx={{ mb: 3 }}>
              <CardHeader>
                <Typography variant="h6">Action Taken</Typography>
              </CardHeader>
              <CardContent>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {insight.action_taken}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Related Insights */}
          {insight.related_insight_ids && insight.related_insight_ids.length > 0 && (
            <Card>
              <CardHeader>
                <Typography variant="h6">Related Insights</Typography>
              </CardHeader>
              <CardContent>
                <List sx={{ py: 0 }}>
                  {insight.related_insight_ids.map((relatedId) => (
                    <ListItem 
                      key={relatedId} 
                      sx={{ pl: 0, cursor: 'pointer' }}
                      onClick={() => navigate(`/app/analysis/${relatedId}`)}
                    >
                      <ListItemText
                        primary={`Insight ${relatedId.slice(-8)}`}
                        secondary="Click to view"
                        sx={{
                          '& .MuiListItemText-primary': {
                            color: 'primary.main',
                            fontSize: '0.9rem'
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}